// Подразбиение inner skinned mesh под pseudo-bodyVox: треугольник не должен
// накрывать несколько ячеек — иначе discard в mesh.frag даёт «рваные» полигоны.
// Рёбра режем с общим кэшем midpoints (watertight).

import {
  BODY_VOX_CUBE,
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
} from "./bodyVox";

function bboxMaxExtent(pos: Float32Array): number {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < pos.length; i += 3) {
    const x = pos[i]!, y = pos[i + 1]!, z = pos[i + 2]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const sx = maxX - minX;
  const sy = maxY - minY;
  const sz = maxZ - minZ;
  return Math.max(sx, sy, sz);
}

/** Объединяет два набора joint/weight (по 4) → топ-4 по весу, нормализация. */
function mergeSkinPair(
  ja: Uint16Array, wa: Float32Array, aBase: number,
  jb: Uint16Array, wb: Float32Array, bBase: number,
  outJ: Uint16Array, outW: Float32Array, outBase: number,
): void {
  const map = new Map<number, number>();
  const add = (joint: number, w: number): void => {
    if (w < 1e-8 || joint < 0) return;
    map.set(joint, (map.get(joint) ?? 0) + w);
  };
  for (let k = 0; k < 4; k++) {
    add(ja[aBase + k]!, wa[aBase + k]!);
    add(jb[bBase + k]!, wb[bBase + k]!);
  }
  const pairs = [...map.entries()].sort((x, y) => y[1] - x[1]).slice(0, 4);
  let sum = 0;
  for (const p of pairs) sum += p[1];
  if (sum < 1e-12) {
    outJ[outBase] = ja[aBase]!;
    outW[outBase] = 1;
    for (let k = 1; k < 4; k++) {
      outJ[outBase + k] = 0;
      outW[outBase + k] = 0;
    }
    return;
  }
  for (let k = 0; k < pairs.length; k++) {
    outJ[outBase + k] = pairs[k]![0];
    outW[outBase + k] = pairs[k]![1] / sum;
  }
  for (let k = pairs.length; k < 4; k++) {
    outJ[outBase + k] = 0;
    outW[outBase + k] = 0;
  }
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}_${b}` : `${b}_${a}`;
}

function dist3(
  pos: Float32Array,
  i: number,
  j: number,
): number {
  const ax = pos[i * 3]!, ay = pos[i * 3 + 1]!, az = pos[i * 3 + 2]!;
  const bx = pos[j * 3]!, by = pos[j * 3 + 1]!, bz = pos[j * 3 + 2]!;
  const dx = ax - bx, dy = ay - by, dz = az - bz;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function maxEdge(
  pos: Float32Array,
  i0: number,
  i1: number,
  i2: number,
): number {
  return Math.max(
    dist3(pos, i0, i1),
    dist3(pos, i1, i2),
    dist3(pos, i2, i0),
  );
}

function appendVertexMid(
  positions: Float32Array,
  normals: Float32Array,
  uvs: Float32Array,
  joints: Uint16Array,
  weights: Float32Array,
  ia: number,
  ib: number,
): { positions: Float32Array; normals: Float32Array; uvs: Float32Array; joints: Uint16Array; weights: Float32Array; newIdx: number } {
  const vCount = positions.length / 3;
  const newIdx = vCount;
  const p = new Float32Array(positions.length + 3);
  p.set(positions);
  const n = new Float32Array(normals.length + 3);
  n.set(normals);
  const uv = new Float32Array(uvs.length + 2);
  uv.set(uvs);
  const jn = new Uint16Array(joints.length + 4);
  jn.set(joints);
  const wn = new Float32Array(weights.length + 4);
  wn.set(weights);

  const o = newIdx * 3;
  const ou = newIdx * 2;
  const oj = newIdx * 4;

  const ia3 = ia * 3, ib3 = ib * 3;
  p[o] = (positions[ia3]! + positions[ib3]!) * 0.5;
  p[o + 1] = (positions[ia3 + 1]! + positions[ib3 + 1]!) * 0.5;
  p[o + 2] = (positions[ia3 + 2]! + positions[ib3 + 2]!) * 0.5;

  const nax = normals[ia3]!, nay = normals[ia3 + 1]!, naz = normals[ia3 + 2]!;
  const nbx = normals[ib3]!, nby = normals[ib3 + 1]!, nbz = normals[ib3 + 2]!;
  let nx = nax + nbx, ny = nay + nby, nz = naz + nbz;
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (nl > 1e-8) {
    nx /= nl;
    ny /= nl;
    nz /= nl;
  } else {
    nx = nax;
    ny = nay;
    nz = naz;
  }
  n[o] = nx;
  n[o + 1] = ny;
  n[o + 2] = nz;

  const ia2 = ia * 2, ib2 = ib * 2;
  uv[ou] = (uvs[ia2]! + uvs[ib2]!) * 0.5;
  uv[ou + 1] = (uvs[ia2 + 1]! + uvs[ib2 + 1]!) * 0.5;

  mergeSkinPair(joints, weights, ia * 4, joints, weights, ib * 4, jn, wn, oj);

  return { positions: p, normals: n, uvs: uv, joints: jn, weights: wn, newIdx };
}

function onePassRefine(
  positions: Float32Array,
  normals: Float32Array,
  uvs: Float32Array,
  joints: Uint16Array,
  weights: Float32Array,
  indices: Uint16Array | Uint32Array,
  maxEdgeThreshold: number,
): {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  joints: Uint16Array;
  weights: Float32Array;
  indices: Uint32Array;
  splitCount: number;
  triIn: number;
  triOut: number;
} {
  const triIn = indices.length / 3;
  let pos = positions;
  let norm = normals;
  let uv = uvs;
  let jn = joints;
  let wn = weights;

  const edgeMid = new Map<string, number>();
  const outIdx: number[] = [];
  let splitCount = 0;

  const getMid = (ia: number, ib: number): number => {
    const k = edgeKey(ia, ib);
    const ex = edgeMid.get(k);
    if (ex !== undefined) return ex;
    const ap = appendVertexMid(pos, norm, uv, jn, wn, ia, ib);
    pos = ap.positions;
    norm = ap.normals;
    uv = ap.uvs;
    jn = ap.joints;
    wn = ap.weights;
    const ni = ap.newIdx;
    edgeMid.set(k, ni);
    return ni;
  };

  for (let t = 0; t < indices.length; t += 3) {
    const i0 = indices[t]!;
    const i1 = indices[t + 1]!;
    const i2 = indices[t + 2]!;
    const me = maxEdge(pos, i0, i1, i2);
    if (me <= maxEdgeThreshold) {
      outIdx.push(i0, i1, i2);
      continue;
    }
    splitCount++;
    const m01 = getMid(i0, i1);
    const m12 = getMid(i1, i2);
    const m20 = getMid(i2, i0);
    // Четыре треугольника (pattern Loop subdivision)
    outIdx.push(i0, m01, m20);
    outIdx.push(m01, i1, m12);
    outIdx.push(m20, m12, i2);
    outIdx.push(m01, m12, m20);
  }

  return {
    positions: pos,
    normals: norm,
    uvs: uv,
    joints: jn,
    weights: wn,
    indices: Uint32Array.from(outIdx),
    splitCount,
    triIn,
    triOut: outIdx.length / 3,
  };
}

/**
 * Дробит длинные рёбра inner skinned mesh в model space, пока maxEdge < порога
 * или достигнут лимит проходов. Порог ≈ размер ячейки bodyVox в тех же единицах.
 */
export function refineInnerSkinMeshForBodyVox(
  positions: Float32Array,
  normals: Float32Array,
  uvs: Float32Array,
  joints: Uint16Array,
  weights: Float32Array,
  indices: Uint16Array | Uint32Array,
  options?: { maxPasses?: number },
): {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  joints: Uint16Array;
  weights: Float32Array;
  indices: Uint16Array | Uint32Array;
} {
  const maxPasses = options?.maxPasses ?? 3;
  const extent = bboxMaxExtent(positions);
  // Оценка «одной ячейки» в локальных координатах меша: bbox по высоте тела
  // сопоставим с высотой сетки в мире (BODY_VOX_SZ * BODY_VOX_CUBE).
  const gridSpanWorld = BODY_VOX_CUBE * Math.max(BODY_VOX_SX, BODY_VOX_SY, BODY_VOX_SZ);
  const maxEdgeThreshold = extent * (BODY_VOX_CUBE / gridSpanWorld) * 1.1;

  let pos = positions;
  let nrm = normals;
  let uv = uvs;
  let jn = joints;
  let wn = weights;
  let ind: Uint16Array | Uint32Array = indices;
  let totalSplits = 0;
  let pass = 0;
  for (; pass < maxPasses; pass++) {
    const r = onePassRefine(pos, nrm, uv, jn, wn, ind, maxEdgeThreshold);
    pos = r.positions;
    nrm = r.normals;
    uv = r.uvs;
    jn = r.joints;
    wn = r.weights;
    ind = r.indices;
    totalSplits += r.splitCount;
    if (r.splitCount === 0) break;
  }

  if (totalSplits === 0) {
    return {
      positions,
      normals,
      uvs,
      joints,
      weights,
      indices,
    };
  }

  const triBefore = indices.length / 3;
  const triAfter = ind.length / 3;
  console.log(
    `[mesh] bodyVox refine: ${triBefore} → ${triAfter} tris (${pass + 1} pass(es), ` +
      `thr≈${maxEdgeThreshold.toFixed(4)}, bboxMax≈${extent.toFixed(3)})`,
  );

  return {
    positions: pos,
    normals: nrm,
    uvs: uv,
    joints: jn,
    weights: wn,
    indices: ind,
  };
}
